import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { LINKS } from "@/lib/constants";

// Hidden onboarding page reached via Shopify webhook email link with ?token=...
// NOTE: This page does NOT persist credentials (per project scope — no DB).
// It captures username/password client-side, then performs smart device routing
// to the proper App Store. Actual account creation occurs via the Shopify webhook
// pipeline that issued the activation link.
const Activate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = "Activate Your SHYFT Coalition™ Account";
  }, []);

  const params = new URLSearchParams(location.search);
  const token = params.get("token") || "";

  const onSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const username = (fd.get("username") || "").toString().trim();
    const password = (fd.get("password") || "").toString();
    const confirm = (fd.get("confirm") || "").toString();

    if (!username || username.length < 3) {
      toast.error("Choose a username with at least 3 characters.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }

    // Smart device detection -> route to correct store
    const ua = navigator.userAgent || navigator.vendor || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);

    toast.success("Credentials secured. Routing to the correct app store…");

    setTimeout(() => {
      if (isIOS) {
        window.location.href = LINKS.appStoreIOS;
      } else if (isAndroid) {
        window.location.href = LINKS.playStoreAndroid;
      } else {
        // Desktop fallback — present both options
        navigate("/activate?stage=desktop", { replace: true });
      }
    }, 900);
  };

  const stage = params.get("stage");

  return (
    <main
      data-testid="activate-page"
      className="relative min-h-screen flex items-center justify-center px-6 py-24"
    >
      <div
        aria-hidden
        className="vault-spotlight"
        style={{ top: "10%", left: "50%", transform: "translateX(-50%)", width: 700, height: 700 }}
      />

      <div className="relative w-full max-w-xl">
        <a
          href="/"
          className="eyebrow inline-flex items-center gap-2 text-[#b8b3aa] hover:text-white"
          data-testid="activate-back"
        >
          ← Back to vault
        </a>

        <div className="mt-6 rounded-[2.5rem] border border-[rgba(201,162,91,0.25)] bg-[#14110e]/85 backdrop-blur p-8 md:p-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(201,162,91,0.4)] bg-[#0a0a0a]/60">
            <ShieldCheck size={14} className="text-gold" />
            <span className="eyebrow !text-[10px] !text-[#e7c98a]">
              Secure Onboarding
            </span>
          </div>

          <h1 className="font-serif-display mt-6 text-4xl md:text-5xl text-white leading-tight">
            Activate your{" "}
            <span className="italic text-gradient-gold">Coalition</span> account.
          </h1>
          <p className="mt-4 text-[#b8b3aa] leading-relaxed">
            Establish your unique credentials. Once submitted, we&rsquo;ll route
            you instantly to the correct app store for your device.
          </p>

          {token ? (
            <div className="mt-6 text-xs text-[#80786c] font-mono break-all border border-[rgba(201,162,91,0.18)] rounded-full px-4 py-2 inline-block">
              Token · {token.slice(0, 16)}…
            </div>
          ) : (
            <div className="mt-6 text-xs text-[#c98e5b] border border-[rgba(201,142,91,0.35)] rounded-full px-4 py-2 inline-block">
              No activation token detected — proceed only if you arrived from
              the activation email.
            </div>
          )}

          {stage === "desktop" ? (
            <div className="mt-10 space-y-5" data-testid="desktop-routing">
              <p className="text-white">
                You appear to be on desktop. Open one of the stores below on
                your phone:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <a
                  href={LINKS.appStoreIOS}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="route-ios"
                  className="btn-outline-gold !justify-between"
                >
                  iOS · App Store
                  <ArrowRight size={16} />
                </a>
                <a
                  href={LINKS.playStoreAndroid}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="route-android"
                  className="btn-outline-gold !justify-between"
                >
                  Android · Play Store
                  <ArrowRight size={16} />
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="activate-form">
              <div>
                <label className="eyebrow !text-[10px]">Username</label>
                <input
                  name="username"
                  type="text"
                  autoComplete="username"
                  data-testid="username-input"
                  className="mt-2 w-full rounded-full bg-[#0a0a0a] border border-[rgba(201,162,91,0.25)] px-5 py-3.5 text-white placeholder-[#5b554c] focus:outline-none focus:border-[#e7c98a] transition"
                  placeholder="shyft_operator"
                  required
                />
              </div>

              <div>
                <label className="eyebrow !text-[10px]">Password</label>
                <input
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  data-testid="password-input"
                  className="mt-2 w-full rounded-full bg-[#0a0a0a] border border-[rgba(201,162,91,0.25)] px-5 py-3.5 text-white placeholder-[#5b554c] focus:outline-none focus:border-[#e7c98a] transition"
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div>
                <label className="eyebrow !text-[10px]">Confirm Password</label>
                <input
                  name="confirm"
                  type="password"
                  autoComplete="new-password"
                  data-testid="confirm-input"
                  className="mt-2 w-full rounded-full bg-[#0a0a0a] border border-[rgba(201,162,91,0.25)] px-5 py-3.5 text-white placeholder-[#5b554c] focus:outline-none focus:border-[#e7c98a] transition"
                  placeholder="Repeat your password"
                  required
                />
              </div>

              <button
                type="submit"
                data-testid="submit-credentials"
                className="btn-gold w-full mt-4"
              >
                <Lock size={16} />
                Submit &amp; Route to App
              </button>

              <p className="text-xs text-[#80786c] text-center leading-relaxed pt-2">
                By submitting you agree to our{" "}
                <a href={LINKS.terms} target="_blank" rel="noreferrer" className="text-gold hover:text-white">
                  Terms
                </a>{" "}
                and acknowledge that subscriptions can only be cancelled by
                emailing{" "}
                <a href={`mailto:${LINKS.supportEmail}`} className="text-gold hover:text-white">
                  {LINKS.supportEmail}
                </a>
                .
              </p>
            </form>
          )}
        </div>
      </div>
    </main>
  );
};

export default Activate;
